import reactUpdate from 'immutability-helper';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import componentName from 'util/componentName';

export interface RowState {
  id: string;
}

export type RowTransformFunc<TRowState extends RowState> = (state: TRowState) => TRowState;
export type UpdateRowStateFunc<TRowState extends RowState> = (
  rowId: string,
  transform: RowTransformFunc<TRowState>
) => void;
export type UpdateEachRowStateFunc<TRowState extends RowState> = (
  transform: RowTransformFunc<TRowState>
) => void;

export interface ManageRowStateProps<TRowState extends RowState> {
  rowStates: Array<TRowState>;
  updateRowState: UpdateRowStateFunc<TRowState>;
  updateEachRowState: UpdateEachRowStateFunc<TRowState>;
}

/**
 * An HOC to maintain state for a list of id'd items.
 * @param {Object} defaultState - state object to use when an item is first added.
 * @param {Object} mapPropsToInitialStateById (optional) - returns the initial state for each row given the
 *                                                         wrapped component's props
 * @param {Function} mapPropsToOrderedIds - returns a list of ordered ids given the wrapped component's props
 * @param {Function} mapStateToProps - returns the props to pass to the wrapped component,
 *                                     given args: rowStates, update(id, op), updateAll(op)
 * @return {Function} - a function to wrap the component class that has the list of items
 */
export default function manageRowState<TProps extends object, TRowState extends RowState>({
  defaultState,
  mapPropsToInitialStateById,
  mapPropsToOrderedIds,
  mapStateToProps,
}: {
  defaultState: Omit<TRowState, 'id'>;
  mapPropsToInitialStateById?: (props: TProps) => Record<string, TRowState>;
  mapPropsToOrderedIds: (props: TProps) => Array<string>;
  mapStateToProps: (
    rowStates: Array<TRowState>,
    updateRowState: UpdateRowStateFunc<TRowState>,
    updateEachRowState: UpdateEachRowStateFunc<TRowState>
  ) => ManageRowStateProps<TRowState>;
}): (
  component: React.ComponentClass<TProps & ManageRowStateProps<TRowState>>
) => React.ComponentClass<TProps> {
  return function makeComponent(
    WrappedComponent: React.ComponentClass<TProps & ManageRowStateProps<TRowState>>
  ): React.ComponentClass<TProps> {
    return class extends React.PureComponent<
      TProps & ManageRowStateProps<TRowState>,
      {
        rowStates: Array<TRowState>;
      }
    > {
      static displayName = `RowStateManager(${componentName(WrappedComponent)})`;

      override UNSAFE_componentWillReceiveProps(nextProps: TProps & ManageRowStateProps<TRowState>): void {
        const oldIds = mapPropsToOrderedIds(this.props);
        const newIds = mapPropsToOrderedIds(nextProps);
        if (!isEqual(oldIds, newIds)) {
          this.setState({
            rowStates: this._makeRowStates(nextProps, this.state.rowStates),
          });
        }
      }

      _makeRowStates = (
        nextProps: TProps & ManageRowStateProps<TRowState>,
        oldStates: Array<TRowState> = []
      ): Array<TRowState> => {
        const ids = mapPropsToOrderedIds(nextProps);
        const oldStatesById = _.indexBy(oldStates, 'id');
        const initialStateById = mapPropsToInitialStateById ? mapPropsToInitialStateById(nextProps) : {};
        const rowStates = ids.map((id) => {
          const initialRowState = (initialStateById && initialStateById[id]) || defaultState;
          return {...(oldStatesById[id] || initialRowState), id};
        });
        return rowStates;
      };

      _update = (id: string, transformRowState: RowTransformFunc<TRowState>): void => {
        const index = this.state.rowStates.findIndex((item) => item.id === id);
        this.setState({
          rowStates: reactUpdate(this.state.rowStates, {[index]: {$apply: transformRowState}}),
        });
      };

      _updateEach = (transformRowState: RowTransformFunc<TRowState>): void => {
        this.setState({
          rowStates: this.state.rowStates.map((value) => transformRowState(value)),
        });
      };

      override state: {rowStates: Array<TRowState>} = {
        rowStates: this._makeRowStates(this.props),
      };

      override render(): JSX.Element {
        return (
          <WrappedComponent
            {...this.props}
            {...mapStateToProps(this.state.rowStates, this._update, this._updateEach)}
          />
        );
      }
    };
  };
}
